/**
 * Shared type + helpers for advisor-uploaded documents.
 *
 * FormShell owns the storage so files persist across step navigation
 * (leaving the AdditionalDocuments step and returning later shouldn't
 * lose an already-picked file) and so the downstream PdfGeneration step
 * can list them alongside filled PDF templates and include them in
 * "Download all". The AdditionalDocuments step is a controlled UI that
 * writes to this store via the callback FormShell threads down.
 */
import type { WorkflowTemplate } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';
import { renderFilename } from './renderFilename';

/** One file inside a named mandatory / optional document entry. */
export type UploadedEntryFile = {
  /** Stable React key — name + size + lastModified + append-count. */
  uid: string;
  /** The actual File object — held so PdfGenerationStep can produce a
   *  blob URL on-demand for Preview / Download. */
  file: File;
};

/** One file inside the free-form optional bucket — carries a description. */
export type UploadedBucketFile = UploadedEntryFile & {
  description: string;
};

/**
 * FormShell-owned store. `entries` keys on `StepFileEntry.id`; `bucket`
 * holds free-form advisor uploads with no per-file entry label.
 */
export type UploadedFilesStore = {
  entries: Record<string, UploadedEntryFile[]>;
  bucket: UploadedBucketFile[];
};

/** Compact view of a named mandatory-doc entry so callers don't have to
 *  re-walk the template step tree — the flattener consumes this and
 *  applies each entry's filename template at packaging time. */
export type UploadedEntryMeta = {
  label: string;
  filenameTemplate?: string | null;
};

/** Flatten every uploaded file into a single list with a display label
 *  per entry — used by PdfGenerationStep to render rows and by the
 *  Download-all handler. Named entries expose their entry label; bucket
 *  files fall back to their filename. When an entry carries a
 *  `filenameTemplate`, the packaged `fileName` runs through the
 *  renderer so the zip archives it as e.g. `Transfer_Form_Smith_...pdf`
 *  instead of the original upload name. The file's original extension
 *  is preserved. Optional bucket files always keep their original name.
 */
export function flattenUploadedFiles(
  store: UploadedFilesStore,
  entryMeta: Record<string, UploadedEntryMeta>,
  values: Record<string, FieldValue> = {},
  workflowTemplate?: WorkflowTemplate,
): Array<{ uid: string; file: File; label: string; fileName: string }> {
  const out: Array<{ uid: string; file: File; label: string; fileName: string }> = [];
  for (const [entryId, files] of Object.entries(store.entries)) {
    const meta = entryMeta[entryId];
    const entryLabel = meta?.label ?? '';
    for (const f of files) {
      const originalExt = extensionOf(f.file.name);
      const packagedName =
        meta?.filenameTemplate && workflowTemplate
          ? renderFilename(
              meta.filenameTemplate,
              entryLabel || f.file.name,
              values,
              workflowTemplate,
              originalExt,
            )
          : f.file.name;
      out.push({
        uid: f.uid,
        file: f.file,
        label: entryLabel || f.file.name,
        fileName: packagedName,
      });
    }
  }
  for (const f of store.bucket) {
    // Optional bucket uploads: keep the original filename verbatim.
    out.push({
      uid: f.uid,
      file: f.file,
      label: f.file.name,
      fileName: f.file.name,
    });
  }
  return out;
}

/** Extract the extension (without the leading dot) from a filename;
 *  defaults to `pdf` when the file has no extension so the packaged
 *  name still lands under a recognisable type. */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0 || dot === fileName.length - 1) return 'pdf';
  return fileName.slice(dot + 1).toLowerCase();
}
